import React from 'react';

import {Stack, Typography, useTheme} from '@mui/material';
import Icon from 'presentation/icons';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';
import {AppointmentInfoListProps} from './types';
import {formatText} from 'presentation/utils/formatText';
import {StyledIconIllustration, StyledStack} from './styles';
import {InfoLines} from './components/InfoLines';
import {translations} from 'presentation/translations-new';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';

const {protocolNumber: protocolNumberText, healthAppointment, exam} = translations['pt-br'].commons;
const {selectedProcedure} = translations['pt-br'].privateFlow.bookingFlow.examSelection;
const {appointmentInstructions} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails;

export function AppointmentInfoList({
	beneficiaryName,
	planName,
	providerName,
	specialtyName,
	date,
	hour,
	protocolNumber,
	clinicName,
	flowType,
	isAdvanceDiagnosticsExam = false,
}: AppointmentInfoListProps) {
	const theme = useTheme();
	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isExam = flowType === FlowEnum.EXAM;

	return (
		<Stack gap={3}>
			<Stack spacing={2} direction="row" alignItems="center">
				<StyledIconIllustration
					customIconName="person-outline"
					variant="primary"
					size="small"
					sx={{p: 1}}
				/>
				<Stack>
					<Typography variant="body1" fontWeight={600}>
						{formatText(beneficiaryName)}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{formatText(planName)}
					</Typography>
				</Stack>
			</Stack>

			<StyledStack gap={2}>
				{isAdvanceDiagnosticsExam ? (
					<InfoLines title={specialtyName} subtitle={selectedProcedure} subtitleFirst />
				) : (
					<InfoLines title={providerName} subtitle={specialtyName} />
				)}

				{isTelehealth ? (
					<Stack>
						<Typography variant="body1" fontWeight={600}>
							{TELEMEDICINE_INFO.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{TELEMEDICINE_INFO.label2}
						</Typography>
					</Stack>
				) : (
					<InfoLines
						title={clinicName ?? ''}
						subtitle={isExam ? exam : healthAppointment}
						subtitleFirst={isAdvanceDiagnosticsExam}
					/>
				)}

				<Stack direction="row" gap={3}>
					<Stack direction="row" gap={1} alignItems="center">
						<Icon name="event-filled" size={20} color={theme.palette.primary.main} />
						<Typography variant="body2">{date}</Typography>
					</Stack>
					<Stack direction="row" gap={1} alignItems="center">
						<Icon
							name="query-builder-filled"
							size={20}
							color={theme.palette.primary.main}
						/>
						<Typography variant="body2">{hour}</Typography>
					</Stack>
				</Stack>
			</StyledStack>

			{protocolNumber ? (
				<Stack gap="10px">
					<Typography variant="subheader">
						{appointmentInstructions.appointmentProtocol}
					</Typography>
					<Stack gap={1} direction="row">
						<Icon
							name="assignment-filled"
							size={20}
							color={theme.palette.primary.main}
						/>
						<Stack>
							<Typography variant="body1" color="text.primary">
								{protocolNumberText}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{protocolNumber}
							</Typography>
						</Stack>
					</Stack>
				</Stack>
			) : null}
		</Stack>
	);
}
